import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject, Subscription, take, takeUntil, combineLatest, finalize, distinctUntilChanged } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AI_CHATBOT_MESSAGE_KEYS, AiChatbotChatRequest, AiChatbotStreamError, ChatMessage, ChatbotVisualState } from '../../models/ai-chatbot.model';
import { AiChatbotService } from '../../services/ai-chatbot.service';
import { BsmTextarea } from '@bsm-ui-lib/bsm-ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RoleName } from '../../../core/user/RoleName';
import { AppUser } from '../../../core/user/AppUser';
import { UserFacade } from '../../../../store/facade/user.facade';
import { AiChatbotFacade } from '../../../../store/facade/ai-chatbot.facade';
import { OrderingFacade } from '../../../../store/facade/ordering.facade';
import { MessageBoxComponent } from '../../../shared/components/message-box/message-box.component';
import { IconComponent } from '../../../shared/components/fa-icon/fa-icon.component';
import { ButtonComponent } from '../../../shared/components/buttons/button/button.component';
import { InputGroupComponent } from '../../../shared/components/inputs/input-group/input-group.component';
import { JeDialogService } from '../../../shared/components/dialog/dialog.service';
import { ConfirmDeleteDialogComponent, ConfirmDeleteDialogData } from '../../../shared/components/confirm-delete/confirm-delete-dialog.component';

@Component({
    selector: 'je-ai-chatbot-dock',
    templateUrl: './ai-chatbot-dock.component.html',
    imports: [CommonModule, BsmTextarea, TranslateModule, MessageBoxComponent, IconComponent, ButtonComponent, InputGroupComponent],
})
export class AiChatbotDockComponent implements OnInit, OnDestroy {
    @ViewChild('messagesContainer')
    private messagesContainer?: ElementRef<HTMLDivElement>;
    @ViewChild('dockPulseTarget')
    private dockPulseTarget?: ElementRef<HTMLButtonElement>;

    public isChatbotVisible = false;
    public isOpen = false;
    public draftMessage = '';
    public visualState: ChatbotVisualState = 'initial';
    public processingDotIndex = 0;
    public readonly useAzureMock = environment.aiChatbotUseAzureMock;
    public messages: ChatMessage[] = [];
    public orderContextConsent: boolean | null = null;
    public orderContextOrderId: number | null = null;
    public streamingAssistantText = '';

    private threadId: string | null = null;
    private orderContextPromptedOrderId: number | null = null;
    private activeOrderId: number | null = null;
    private readonly operativeRoles = new Set<number>([RoleName.Disponent, RoleName.TeamDisponent, RoleName.Surveyor, RoleName.QualityManager, RoleName.BackOffice]);
    private readonly contractorGroups = new Set<string>([AppUser.GROUP_CONTRACTOR, AppUser.GROUP_CONTRACTOR_ADMIN]);
    private readonly destroy$ = new Subject<void>();
    private processingDotsIntervalId?: number;
    private requestInFlight = false;
    private activeChatRequest?: Subscription;
    private pendingStreamingAssistantText = '';
    private streamingRenderFrameId?: number;
    private streamingScrollFrameId?: number;
    private shouldAutoScrollStreamingMessages = true;

    constructor(
        private aiChatbotService: AiChatbotService,
        private userFacade: UserFacade,
        private aiChatbotFacade: AiChatbotFacade,
        private orderingFacade: OrderingFacade,
        private dialog: JeDialogService,
        private translateService: TranslateService,
    ) {}

    public ngOnInit(): void {
        this.aiChatbotFacade.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
            const wasSending = this.isSending;
            this.isOpen = state.isOpen;
            this.threadId = state.threadId;
            this.visualState = state.visualState;
            this.messages = [...state.messages];
            this.orderContextOrderId = state.orderContextOrderId;
            this.orderContextConsent = state.orderContextConsent;
            this.orderContextPromptedOrderId = state.orderContextPromptedOrderId;

            if (!wasSending && this.isSending) {
                this.startProcessingDotsAnimation();
            } else if (wasSending && !this.isSending) {
                this.stopProcessingDotsAnimation();
            }

            if (this.ensureInitialGreetingMessage()) {
                return;
            }

            this.ensureOrderContextConsentPrompt();
        });

        combineLatest([this.userFacade.currentRoleId$, this.userFacade.userGroup$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([roleId, userGroup]) => {
                const wasVisible = this.isChatbotVisible;
                this.isChatbotVisible = this.operativelyEligible(roleId, userGroup);
                if (!wasVisible && this.isChatbotVisible) {
                    requestAnimationFrame(() => this.playDockZoomAnimation());
                }

                if (!this.isChatbotVisible) {
                    this.aiChatbotFacade.setOpen(false);
                }
            });

        this.orderingFacade.orderId$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(orderId => {
            const normalizedOrderId = orderId > 0 ? orderId : null;
            this.activeOrderId = normalizedOrderId;
            this.aiChatbotFacade.setOrderContext(normalizedOrderId);
        });
    }

    public ngOnDestroy(): void {
        this.stopStreaming();
        this.cancelStreamingUiFrames();
        this.stopProcessingDotsAnimation();
        this.destroy$.next();
        this.destroy$.complete();
    }

    public toggleOpen(): void {
        const nextOpenState = !this.isOpen;
        this.aiChatbotFacade.setOpen(nextOpenState);
        if (nextOpenState) {
            this.scrollMessagesToBottom(true);
        }
    }

    public close(): void {
        this.aiChatbotFacade.setOpen(false);
    }

    public startNewChat(): void {
        if (this.isSending) {
            return;
        }

        const dialogData: ConfirmDeleteDialogData = {
            title: 'AI_CHATBOT.DIALOG.NEW_CHAT.TITLE',
            message: 'AI_CHATBOT.DIALOG.NEW_CHAT.MESSAGE',
            cancelLabel: 'AI_CHATBOT.DIALOG.NEW_CHAT.CANCEL',
            confirmLabel: 'AI_CHATBOT.DIALOG.NEW_CHAT.CONFIRM',
            confirmIcon: 'comment-plus',
            confirmVariant: 'primary',
        };

        const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
            disableClose: false,
            data: dialogData,
        });

        dialogRef.afterClosed$.pipe(take(1), takeUntil(this.destroy$)).subscribe(result => {
            if (result !== true) {
                return;
            }

            this.draftMessage = '';
            this.aiChatbotFacade.resetState();
            this.aiChatbotFacade.setOpen(true);
            this.aiChatbotFacade.setOrderContext(this.activeOrderId);
            this.scrollMessagesToBottom(true);
        });
    }

    public sendMessage(): void {
        const chatMessage = this.draftMessage.trim();
        if (!chatMessage || this.isSending || this.requestInFlight || this.isOrderContextConsentPending) {
            return;
        }

        this.appendMessage({ role: 'user', text: chatMessage });
        this.draftMessage = '';
        this.streamingAssistantText = '';
        this.pendingStreamingAssistantText = '';
        this.aiChatbotFacade.setVisualState('processing');
        this.scrollMessagesToBottom(true);
        this.requestInFlight = true;

        const payload: AiChatbotChatRequest = {
            threadId: this.threadId,
            chatMessage,
            orderId: this.orderContextConsent === true ? this.orderContextOrderId : null,
        };

        this.activeChatRequest = this.aiChatbotService
            .chatStreamWithFallback(payload, this.useAzureMock)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.requestInFlight = false;
                    this.activeChatRequest = undefined;
                }),
            )
            .subscribe({
                next: event => {
                    if (event.type === 'thread_started' && event.threadId) {
                        this.aiChatbotFacade.setThreadId(event.threadId);
                        return;
                    }

                    if (event.type === 'delta') {
                        this.queueStreamingAssistantRender(event.responseText ?? '');
                        return;
                    }

                    if (event.type === 'completed') {
                        if (event.threadId) {
                            this.aiChatbotFacade.setThreadId(event.threadId);
                        }

                        const streamedText = this.flushStreamingAssistantRender();
                        const responseText = (event.responseText ?? streamedText).trim();
                        this.streamingAssistantText = '';
                        this.pendingStreamingAssistantText = '';
                        this.appendMessage({
                            role: 'assistant',
                            text: responseText || this.translate(AI_CHATBOT_MESSAGE_KEYS.noResponse),
                        });
                        this.aiChatbotFacade.setVisualState('success');
                    }
                },
                error: (streamError: AiChatbotStreamError) => {
                    const streamedText = this.flushStreamingAssistantRender();
                    const partialResponse = (streamError?.partialResponseText ?? streamedText).trim();
                    if (partialResponse) {
                        this.appendMessage({ role: 'assistant', text: partialResponse });
                    }

                    this.streamingAssistantText = '';
                    this.pendingStreamingAssistantText = '';
                    this.appendMessage({
                        role: 'system',
                        text: this.translate(streamError?.message || 'AI_CHATBOT.ERROR.REQUEST_FAILED'),
                    });
                    this.aiChatbotFacade.setVisualState('initial');
                },
            });
    }

    public stopStreaming(): void {
        if (!this.activeChatRequest) {
            return;
        }

        const partialResponse = this.flushStreamingAssistantRender().trim();
        this.activeChatRequest.unsubscribe();
        this.appendMessage({
            role: 'assistant',
            text: partialResponse || this.translate(AI_CHATBOT_MESSAGE_KEYS.streamStopped),
        });
        this.streamingAssistantText = '';
        this.pendingStreamingAssistantText = '';
        this.aiChatbotFacade.setVisualState('success');
    }

    public onInputKeydown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    public onMessagesScroll(): void {
        const container = this.messagesContainer?.nativeElement;
        if (!container) {
            return;
        }

        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        this.shouldAutoScrollStreamingMessages = distanceFromBottom <= 24;
    }

    public get isSending(): boolean {
        return this.visualState === 'processing' || this.requestInFlight;
    }

    public get isOrderContextConsentPending(): boolean {
        return !!this.orderContextOrderId && this.orderContextConsent === null;
    }

    public get isInputDisabled(): boolean {
        return this.isSending || this.isOrderContextConsentPending;
    }

    public get dockAriaLabel(): string {
        if (this.visualState === 'processing') {
            return this.translate('AI_CHATBOT.UI.DOCK.ARIA_PROCESSING');
        }

        return this.isOpen ? this.translate('AI_CHATBOT.UI.DOCK.ARIA_CLOSE') : this.translate('AI_CHATBOT.UI.DOCK.ARIA_OPEN');
    }

    public setOrderContextConsent(consent: boolean): void {
        if (!this.orderContextOrderId || this.orderContextConsent !== null) {
            return;
        }

        this.aiChatbotFacade.setOrderContextConsent(consent);
        this.appendMessage({
            role: 'assistant',
            text: this.translate(consent ? AI_CHATBOT_MESSAGE_KEYS.orderContextAccepted : AI_CHATBOT_MESSAGE_KEYS.orderContextRejected),
        });
    }

    private appendMessage(message: ChatMessage): void {
        if (!message?.text) {
            return;
        }

        this.aiChatbotFacade.appendMessage(message);
        this.scrollMessagesToBottom();
    }

    private scrollMessagesToBottom(force = false): void {
        if (!force && !this.shouldAutoScrollStreamingMessages) {
            return;
        }

        if (force) {
            this.shouldAutoScrollStreamingMessages = true;
        }

        const scrollToBottom = () => {
            const container = this.messagesContainer?.nativeElement;
            if (!container) {
                return;
            }

            container.scrollTop = container.scrollHeight;
        };

        // Trigger scrolling across multiple render ticks so the processing "..."
        // indicator is reliably visible immediately after send.
        queueMicrotask(scrollToBottom);
        setTimeout(scrollToBottom, 0);
        requestAnimationFrame(() => scrollToBottom());
    }

    private queueStreamingAssistantRender(nextResponseText: string): void {
        this.pendingStreamingAssistantText = nextResponseText;
        if (this.streamingRenderFrameId) {
            return;
        }

        this.streamingRenderFrameId = requestAnimationFrame(() => {
            this.streamingRenderFrameId = undefined;
            this.streamingAssistantText = this.pendingStreamingAssistantText;
            this.scheduleStreamingScrollToBottom();
        });
    }

    private flushStreamingAssistantRender(): string {
        if (this.streamingRenderFrameId) {
            cancelAnimationFrame(this.streamingRenderFrameId);
            this.streamingRenderFrameId = undefined;
        }

        if (this.pendingStreamingAssistantText !== this.streamingAssistantText) {
            this.streamingAssistantText = this.pendingStreamingAssistantText;
        }

        return this.streamingAssistantText;
    }

    private scheduleStreamingScrollToBottom(): void {
        if (this.streamingScrollFrameId || !this.shouldAutoScrollStreamingMessages) {
            return;
        }

        this.streamingScrollFrameId = requestAnimationFrame(() => {
            this.streamingScrollFrameId = undefined;
            const container = this.messagesContainer?.nativeElement;
            if (!container) {
                return;
            }
            container.scrollTop = container.scrollHeight;
        });
    }

    private cancelStreamingUiFrames(): void {
        if (this.streamingRenderFrameId) {
            cancelAnimationFrame(this.streamingRenderFrameId);
            this.streamingRenderFrameId = undefined;
        }

        if (this.streamingScrollFrameId) {
            cancelAnimationFrame(this.streamingScrollFrameId);
            this.streamingScrollFrameId = undefined;
        }
    }

    private operativelyEligible(roleId: number, userGroup: string): boolean {
        return this.operativeRoles.has(roleId) && this.contractorGroups.has(userGroup);
    }

    private ensureInitialGreetingMessage(): boolean {
        if (this.messages.length > 0) {
            return false;
        }

        this.aiChatbotFacade.appendMessage({
            role: 'assistant',
            text: this.translate(AI_CHATBOT_MESSAGE_KEYS.initialGreeting),
        });
        this.scrollMessagesToBottom(true);
        return true;
    }

    private ensureOrderContextConsentPrompt(): void {
        if (!this.orderContextOrderId || this.orderContextConsent !== null) {
            return;
        }

        if (this.hasOrderContextConsentPromptMessage) {
            if (this.orderContextPromptedOrderId !== this.orderContextOrderId) {
                this.aiChatbotFacade.setOrderContextPrompted(this.orderContextOrderId);
            }
            return;
        }

        if (this.orderContextPromptedOrderId === this.orderContextOrderId) {
            return;
        }

        this.aiChatbotFacade.setOrderContextPrompted(this.orderContextOrderId);
        this.aiChatbotFacade.appendMessage({
            role: 'assistant',
            text: this.translate(AI_CHATBOT_MESSAGE_KEYS.orderContextConsentPrompt),
        });
        this.scrollMessagesToBottom();
    }

    private get hasOrderContextConsentPromptMessage(): boolean {
        const promptText = this.translate(AI_CHATBOT_MESSAGE_KEYS.orderContextConsentPrompt);
        return this.messages.some(message => message.role === 'assistant' && message.text === promptText);
    }

    private translate(keyOrText: string): string {
        return this.translateService.instant(keyOrText);
    }

    private startProcessingDotsAnimation(): void {
        if (this.processingDotsIntervalId) {
            return;
        }

        this.processingDotIndex = 0;
        this.processingDotsIntervalId = window.setInterval(() => {
            this.processingDotIndex = (this.processingDotIndex + 1) % 3;
        }, 230);
    }

    private stopProcessingDotsAnimation(): void {
        if (!this.processingDotsIntervalId) {
            this.processingDotIndex = 0;
            return;
        }

        window.clearInterval(this.processingDotsIntervalId);
        this.processingDotsIntervalId = undefined;
        this.processingDotIndex = 0;
    }

    private playDockZoomAnimation(): void {
        const dock = this.dockPulseTarget?.nativeElement;
        if (!dock || typeof dock.animate !== 'function') {
            return;
        }

        dock.getAnimations().forEach(animation => animation.cancel());
        dock.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.28)' }, { transform: 'scale(1)' }], { duration: 480, easing: 'ease-in-out', iterations: 3 });
    }
}
